const CURRENT_TENANT_UUID_STORAGE_KEY = 'currentTenantUuid';
const CURRENT_ORGANIZATION_UUID_STORAGE_KEY = 'currentOrganizationUuid';
const LEGACY_CURRENT_PARK_UUID_STORAGE_KEY = 'currentParkUuid';
const CURRENT_PARK_UUID_BY_TENANT_STORAGE_KEY = 'currentParkUuidByTenant';

let tenantContextGeneration = 0;

export const getTenantContextGeneration = () => tenantContextGeneration;

export const resetTenantContextGenerationForTests = () => {
  tenantContextGeneration = 0;
};

const bumpTenantContextGenerationIfChanged = (
  previous: string | null,
  next: string | null,
) => {
  if (previous !== next) {
    tenantContextGeneration += 1;
  }
};

export const saveCurrentTenantUuid = (tenantUuid?: string | null) => {
  try {
    const previous = localStorage.getItem(CURRENT_TENANT_UUID_STORAGE_KEY);
    const next = tenantUuid || null;
    bumpTenantContextGenerationIfChanged(previous, next);
    if (next) {
      localStorage.setItem(CURRENT_TENANT_UUID_STORAGE_KEY, next);
      return;
    }
    localStorage.removeItem(CURRENT_TENANT_UUID_STORAGE_KEY);
  } catch {
    // ignore storage access errors
  }
};

export const saveCurrentOrganizationUuid = (
  organizationUuid?: string | null,
) => {
  try {
    const previous = localStorage.getItem(
      CURRENT_ORGANIZATION_UUID_STORAGE_KEY,
    );
    const next = organizationUuid || null;
    bumpTenantContextGenerationIfChanged(previous, next);
    if (next) {
      localStorage.setItem(CURRENT_ORGANIZATION_UUID_STORAGE_KEY, next);
      return;
    }
    localStorage.removeItem(CURRENT_ORGANIZATION_UUID_STORAGE_KEY);
  } catch {
    // ignore storage access errors
  }
};

export const readCurrentOrganizationUuid = () => {
  try {
    return localStorage.getItem(CURRENT_ORGANIZATION_UUID_STORAGE_KEY);
  } catch {
    return null;
  }
};

export const readCurrentTenantUuid = () => {
  try {
    return localStorage.getItem(CURRENT_TENANT_UUID_STORAGE_KEY);
  } catch {
    return null;
  }
};

const readCurrentParkUuidByTenant = (): Record<string, string> => {
  try {
    const value = localStorage.getItem(CURRENT_PARK_UUID_BY_TENANT_STORAGE_KEY);
    if (!value) return {};
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
      return {};
    return Object.fromEntries(
      Object.entries(parsed).filter(
        ([tenantUuid, parkUuid]) =>
          typeof tenantUuid === 'string' &&
          typeof parkUuid === 'string' &&
          Boolean(tenantUuid) &&
          Boolean(parkUuid),
      ),
    ) as Record<string, string>;
  } catch {
    return {};
  }
};

const saveCurrentParkUuidByTenant = (parks: Record<string, string>) => {
  if (Object.keys(parks).length === 0) {
    localStorage.removeItem(CURRENT_PARK_UUID_BY_TENANT_STORAGE_KEY);
    return;
  }
  localStorage.setItem(
    CURRENT_PARK_UUID_BY_TENANT_STORAGE_KEY,
    JSON.stringify(parks),
  );
};

export const saveCurrentParkUuid = (
  parkUuid?: string | null,
  tenantUuid = readCurrentTenantUuid(),
) => {
  try {
    if (!tenantUuid) return;
    const parks = readCurrentParkUuidByTenant();
    if (parkUuid) parks[tenantUuid] = parkUuid;
    else delete parks[tenantUuid];
    saveCurrentParkUuidByTenant(parks);
  } catch {
    // ignore storage access errors
  }
};

export const readCurrentParkUuid = (tenantUuid = readCurrentTenantUuid()) => {
  try {
    if (!tenantUuid) return null;
    const parks = readCurrentParkUuidByTenant();
    const saved = parks[tenantUuid];
    if (saved) return saved;

    const legacyParkUuid = localStorage.getItem(
      LEGACY_CURRENT_PARK_UUID_STORAGE_KEY,
    );
    if (!legacyParkUuid) return null;

    parks[tenantUuid] = legacyParkUuid;
    saveCurrentParkUuidByTenant(parks);
    localStorage.removeItem(LEGACY_CURRENT_PARK_UUID_STORAGE_KEY);
    return legacyParkUuid;
  } catch {
    return null;
  }
};

export const resolveCurrentParkUuid = (
  parks: Array<{ uuid: string }>,
  storedParkUuid?: string | null,
  preferredParkUuid?: string | null,
) => {
  if (storedParkUuid && parks.some((park) => park.uuid === storedParkUuid)) {
    return storedParkUuid;
  }
  if (
    preferredParkUuid &&
    parks.some((park) => park.uuid === preferredParkUuid)
  ) {
    return preferredParkUuid;
  }
  if (parks.length === 1) {
    return parks[0]?.uuid;
  }
  return undefined;
};
